import { useEffect, useRef } from 'react'
import { useThemeMode } from '@pushly/aqe'
import type { SelectedThemeMode } from '@pushly/aqe'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../stores/app'
import { THEME_MODE_STORAGE_KEY, THEME_MODE_PREF_PATH } from './theme-provider'

function isValidThemeMode(val: unknown): val is SelectedThemeMode {
    return val === 'light' || val === 'dark' || val === 'system'
}

/**
 * Bridges the theme mode context with the user preferences API.
 *
 * - On mount: reads the server preference and syncs it into ThemeModeProvider
 * - On change: persists the selected mode to both localStorage and the API
 *
 * Must be rendered inside both ThemeModeProvider and UserProvider.
 */
export const ThemePreferenceSync: React.FC = () => {
    const appState = useService(AppState)
    const userId = appState.currentUser?.id
    const { selectedMode, setSelectedMode } = useThemeMode()
    const prefs = useUserPreferences(userId)
    const hasSyncedFromServer = useRef(false)
    const prevSelectedMode = useRef(selectedMode)

    // On mount / when preferences load: sync server value → theme context.
    // Only runs once we have a userId and preferences have finished loading.
    useEffect(() => {
        if (!userId || prefs.isLoading || hasSyncedFromServer.current) return

        const serverMode = prefs.get(THEME_MODE_PREF_PATH)
        hasSyncedFromServer.current = true

        if (isValidThemeMode(serverMode) && serverMode !== selectedMode) {
            setSelectedMode(serverMode)
            try {
                localStorage.setItem(THEME_MODE_STORAGE_KEY, serverMode)
            } catch {
                // localStorage unavailable
            }
        }
    }, [userId, prefs.isLoading])

    // On user change: persist to localStorage + API.
    // Only fires after server sync and when selectedMode actually changes.
    useEffect(() => {
        if (!hasSyncedFromServer.current) return
        if (prevSelectedMode.current === selectedMode) return
        prevSelectedMode.current = selectedMode

        try {
            localStorage.setItem(THEME_MODE_STORAGE_KEY, selectedMode)
        } catch {
            // localStorage unavailable
        }

        prefs.setUnscoped(THEME_MODE_PREF_PATH, selectedMode)
    }, [selectedMode])

    return null
}
